import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { Component, OnDestroy, OnInit, Input } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { map, switchMap, takeUntil, tap } from 'rxjs/operators';

import { ShopKeys } from '@config/keys/shop.keys';

import { IPongoAsset } from '@core/models/pongo.model';
import { DoodProductModel } from '@core/models/product.model';
import { DoodShopModel } from '@core/models/shop.model';

import { CartService } from '@core/services/cart/cart.service';
import { ModalsService } from '@core/services/modals/modals.service';
import { PongoService } from '@core/services/pongo/pongo.service';

import { CartStoreSelector } from '@common/selectors/cart.selector';
import { ModalStoreSelector } from '@common/selectors/modal.selector';
import { ShopStoreSelector } from '@common/selectors/shop.selector';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ModalScrollBlockBase } from '@core/base/modalScrollBlock/modal-scroll-block.base';
import { UntypedModalIO } from '@core/services/modals/modals-util.type';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { ProductAddModalConfig } from '@shared/modals/product-add-modal/product-add-modal.component';
import { CART_ACTIONS } from '@store/cart';
import { PRODUCT_STORE_COMPONENT_ACTIONS } from '@store/product';

@Component({
  selector: 'app-pongo-offered-product-picker-modal',
  templateUrl: './pongo-offered-product-picker-modal.component.html',
  standalone: false,
})
export class PongoOfferedProductPickerModalComponent
  extends ModalScrollBlockBase
  implements OnInit, OnDestroy
{
  private _destroyerRef = new Subject<boolean>();

  static handle = 'pongo-offered-product-picker-modal';

  @Input() backgroundColor?: IContentBuilderFieldColor;
  static onProductSelected?: () => void;
  static asset?: IPongoAsset;

  index$: Observable<number> = this.modalSelector
    .selectModal(PongoOfferedProductPickerModalComponent.handle)
    .pipe(map(el => (el?.index ? el.index : 6)));

  products: DoodProductModel[] = [];
  isProductsLoading = true;

  private shopId?: string;

  constructor(
    private cartSelector: CartStoreSelector,
    private shopSelector: ShopStoreSelector,
    private modalSelector: ModalStoreSelector,
    private readonly cartService: CartService,
    private readonly pongoService: PongoService,
    private readonly modalsService: ModalsService<ProductAddModalConfig>,
    private readonly store: Store,
    private readonly actions$: Actions,
  ) {
    super();

    this.actions$
      .pipe(ofType(CART_ACTIONS.updateProductInCart), takeUntilDestroyed())
      .subscribe(({ action }) => {
        if (action === 'add' || action === 'increase') {
          this.close();
        }
      });
  }

  ngOnInit(): void {
    this.getShopFromCart$()
      .pipe(
        switchMap(shopId => this.getAllowedProducts$(shopId)),
        takeUntil(this._destroyerRef),
      )
      .subscribe();
    this.getAllowedProducts$();
  }

  private getShopFromCart$(): Observable<undefined | string> {
    return this.cartSelector.selectActive$.pipe(
      map(cart => {
        if (!cart) {
          return undefined;
        }
        return cart?.shop;
      }),
    );
  }

  private getAllowedProducts$(shopId?: string): Observable<DoodShopModel | undefined> {
    if (!shopId) {
      return of(undefined);
    }

    this.shopId = shopId;

    return this.shopSelector.selectShop(shopId).pipe(
      takeUntil(this._destroyerRef),
      tap(shop => {
        const assetExternalId = PongoOfferedProductPickerModalComponent.asset?.external_ids;
        if (!assetExternalId) {
          this.products = [];
          this.isProductsLoading = false;
          return;
        }
        this.pongoService
          .allowedProducts$(assetExternalId, shop?.[ShopKeys.Id])
          .subscribe(products => {
            this.products = products;
            this.isProductsLoading = false;
          });
      }),
    );
  }

  ngOnDestroy(): void {
    this._destroyerRef.next(true);
    this._destroyerRef.complete();
    super.ngOnDestroy();
  }

  close(): void {
    this.modalsService.close<UntypedModalIO>(PongoOfferedProductPickerModalComponent.handle);
  }

  onNavigateTo(product: DoodProductModel): void {
    this.store.dispatch(
      PRODUCT_STORE_COMPONENT_ACTIONS.selectProduct({
        productId: product.id,
        productShopId: product.shop_id,
        productOrigin: {
          channel: 'loyalty',
          reference: 'pongo',
        },
      }),
    );
  }

  addVoucherWithoutProduct(): void {
    this.close();
    if (PongoOfferedProductPickerModalComponent.onProductSelected) {
      PongoOfferedProductPickerModalComponent.onProductSelected();
    }
  }

  cancel(): void {
    const asset = PongoOfferedProductPickerModalComponent.asset;
    if (!asset) {
      this.close();
      return;
    }
    const coupon = { code: asset.hash };
    if (!coupon) {
      return;
    }
    this.cartService.removeCoupon(coupon);
    this.close();
  }
}
